import React, { useState, useEffect, useRef } from "react";
import creatorImg from "../assets/Creator.jpeg";
import logoImg from "../assets/Logo.jpeg";
import { Link, useNavigate } from "react-router-dom";
import '../styles/creator.css';
import Seo from './Seo';

export default function Creator() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  
  const creatorImageRef = useRef(null);
  const creatorTitleRef = useRef(null);
  const creatorContentRef = useRef([]);

  const navItems = [
    { name: "Home", href: "/" },
    { name: "The Game", href: "/#about-game" },
    { name: "Locations", href: "/#locations" },
    { name: "FAQs", href: "/#faqs" },
    { name: "Blog", href: "/blog" },
    { name: "The Architect", href: "/creator" }
  ];

  const handleSectionNavigation = (href) => {
    if (href.startsWith('/#')) {
      const sectionId = href.substring(2);
      navigate('/', { replace: false });
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // Scroll-aware nav
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '-30px 0px -30px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          entry.target.classList.remove('animate-out');
        } else {
          entry.target.classList.add('animate-out');
          entry.target.classList.remove('animate-in');
        }
      });
    }, observerOptions);

    const elementsToObserve = [
      creatorImageRef.current,
      creatorTitleRef.current,
      ...creatorContentRef.current
    ].filter(Boolean);

    elementsToObserve.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  return (
    <div className="page-creator">
      <Seo
        path="/creator"
        title="About Mystery Mansion | The Story Behind the Experience"
        description="Discover the idea and team behind Mystery Mansion, an immersive open-world mystery experience designed for groups during private villa staycations."
      />


      {/* ===== NAVIGATION BAR ===== */}
      <nav className={`fixed top-0 left-0 right-0 z-50 mm-nav ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-18 md:h-20">
            <div className="flex-shrink-0">
              <Link to="/">
                <img 
                  src={logoImg} 
                  alt="Mystery Mansion Logo" 
                  className="h-14 sm:h-16 md:h-20 w-auto cursor-pointer my-0"
                  style={{ filter: 'drop-shadow(0 0 20px rgba(201, 168, 76, 0.1))' }}
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-2">
                {navItems.map((item, index) => (
                  item.href.startsWith('/#') ? (
                    <button
                      key={index}
                      onClick={() => handleSectionNavigation(item.href)}
                      className="mm-nav-link bg-transparent border-none cursor-pointer"
                    >
                      {item.name}
                    </button>
                  ) : item.href.startsWith('#') ? (
                    <a
                      key={index}
                      href={item.href}
                      className="mm-nav-link"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={index}
                      to={item.href}
                      className="mm-nav-link"
                    >
                      {item.name}
                    </Link>
                  )
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="focus:outline-none p-2"
                aria-label="Toggle menu"
                style={{ color: 'var(--text-secondary)' }}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <div className={`md:hidden mm-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item, index) => (
                item.href.startsWith('/#') ? (
                  <button
                    key={index}
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleSectionNavigation(item.href);
                    }}
                    className="mm-nav-link block py-3 bg-transparent border-none cursor-pointer w-full text-left"
                  >
                    {item.name}
                  </button>
                ) : item.href.startsWith('#') ? (
                  <a
                    key={index}
                    href={item.href}
                    className="mm-nav-link block py-3"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={index}
                    to={item.href}
                    className="mm-nav-link block py-3"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* ===== MAIN CONTENT ===== */}
      <section className="creator-section">
        <div className="relative z-10 pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            
            {/* Title */}
            <h1 
              ref={creatorTitleRef}
              className="scroll-animate-up text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-center mb-4 sm:mb-6 px-2"
              style={{ fontFamily: "var(--font-heading)", color: 'var(--text-primary)' }}
            >
              {"The Architect".split('').map((letter, index) => (
                <span key={index} className="wavy-letter">
                  {letter === ' ' ? '\u00A0' : letter}
                </span>
              ))}
            </h1>

            <div className="mm-gold-divider" style={{ marginBottom: '2.5rem' }}></div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
              
              {/* Creator Image */}
              <div 
                ref={creatorImageRef}
                className="scroll-animate-scale text-center lg:text-left"
              >
                <img
                  src={creatorImg}
                  alt="Eshan - Creator of Mystery Mansion"
                  className="creator-image mx-auto lg:mx-0"
                />
                
                {/* Creator Name */}
                <div className="mt-4 sm:mt-6 text-center lg:text-left">
                  <h2 
                    className="text-xl sm:text-2xl md:text-3xl font-bold mb-2"
                    style={{ fontFamily: "var(--font-heading)", color: 'var(--gold)' }}
                  >
                    Eshan
                  </h2>
                  <p 
                    className="text-sm sm:text-base md:text-lg lg:text-xl"
                    style={{ fontFamily: "var(--font-body)", color: 'var(--text-secondary)', letterSpacing: '0.08em' }}
                  >
                    Gamer · Dreamer · Builder
                  </p>
                </div>
              </div>

              {/* Creator Story */}
              <div className="space-y-4 sm:space-y-6">
                
                {/* Journey Card */}
                <div 
                  ref={el => creatorContentRef.current[0] = el}
                  className="scroll-animate content-card"
                >
                  <h3 
                    className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6 text-center"
                    style={{ fontFamily: "var(--font-heading)", color: 'var(--gold-light)' }}
                  >
                    The Journey
                  </h3>
                  
                  {/* Stats Cards */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
                    <div className="stat-card">
                      <span className="stat-number">3</span>
                      <span className="stat-label">Unique Stories</span>
                    </div>
                    <div className="stat-card">
                      <span className="stat-number">15</span>
                      <span className="stat-label">Games Hosted</span>
                    </div>
                    <div className="stat-card">
                      <span className="stat-number">180</span>
                      <span className="stat-label">Players at ISB</span>
                    </div>
                  </div>
                  
                  {/* Journey Content */}
                  <p 
                    className="text-xs sm:text-sm md:text-base leading-relaxed mb-3 sm:mb-4"
                    style={{ fontFamily: "var(--font-body)", color: 'var(--text-secondary)' }}
                  >
                    Even before school, I was swapping cassettes, playing video games. Mario, Duck Hunt, and Contra soon gave way to Lion King, NFS, and Prince of Persia. But my love for thrill wasn't limited to the digital world—I loved creating board games, and reading novels like Goosebumps and Agatha Christie. I was also drawn to mind-bending thrillers like Glass Onion, Coherence, and The Sixth Sense, always envious of the characters who got to live the mysteries and experience the thrill firsthand. Then I finally found the perfect blend of them all.
                  </p>
                  <p 
                    className="text-xs sm:text-sm md:text-base leading-relaxed mb-3 sm:mb-4"
                    style={{ fontFamily: "var(--font-body)", color: 'var(--text-secondary)' }}
                  >
                    Mystery Mansion started as a quirky birthday party idea for a group of five at ISB. It quickly grew into one of the campus' most popular activities. And now I am sharing this immersive experience with the world, giving people a chance to be Holmes, Poirot, and Benoit Blanc themselves.
                  </p>
                  <p 
                    className="text-xs sm:text-sm md:text-base leading-relaxed"
                    style={{ fontFamily: "var(--font-body)", color: 'var(--text-secondary)' }}
                  >
                    I'm bringing the game to different locations, blending local culture and aesthetics of villas with the stories people become a part of for the night.
                  </p>
                  <p 
                    className="text-xs sm:text-sm md:text-base leading-relaxed mt-3 sm:mt-4 font-bold"
                    style={{ fontFamily: "var(--font-body)", color: 'var(--gold)' }}
                  >
                    New villa. New story. New thrill.
                  </p>
                </div>

                {/* Quote */}
                <div 
                  ref={el => creatorContentRef.current[1] = el}
                  className="scroll-animate quote text-xs sm:text-sm md:text-base"
                >
                  Through this game, I want people to not just watch thriller movies, but be a part of them and live them, for the entire stay!
                </div>

                {/* CTA */}
                <div 
                  ref={el => creatorContentRef.current[2] = el}
                  className="scroll-animate text-center lg:text-left pt-2 sm:pt-4"
                >
                  <button 
                    onClick={() => handleSectionNavigation('/#locations')}
                    className="mm-cta text-sm sm:text-base"
                  >
                    Experience the Mystery
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}